import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { appendKeys, getCredentials, getInstance, markRecovered, resendNotification } from "../../api/fleet";
import { createKiroApiKey, recommendUsers } from "../../api/users";
import type { CopyableFieldDto, FleetInstanceViewDto, RecommendUserCandidateDto } from "../../api/types";
import { useAsync } from "../../hooks/useAsync";
import { useAuth } from "../../auth/AuthContext";
import { formatBeijingDate, formatBeijingTime } from "../../utils/datetime";

const MAX_APPEND_KEYS = 100;
const REVEAL_TIMEOUT_MS = 15000;
const TIERS = ["pro", "proplus", "promax", "power"];

interface KeyEntryDraft {
  key: string;
  email: string;
}

function emptyKeyEntry(): KeyEntryDraft {
  return { key: "", email: "" };
}

/** 简单邮箱格式校验,与 backend validators.validate_assigned_user_email 的规则对齐。 */
function isValidEmail(email: string): boolean {
  if (!email || email.length > 254) return false;
  const at = email.indexOf("@");
  if (at <= 0 || at !== email.lastIndexOf("@")) return false;
  return at < email.length - 1;
}

/**
 * 一键复制按钮,`navigator.clipboard.writeText` 实现,禁用态用于 READY 之外
 * 状态下的占位符字段(Requirement 5.6)。
 */
function CopyButton({ field }: { field: CopyableFieldDto }) {
  const [copied, setCopied] = useState(false);
  if (!field.copyable) {
    return (
      <button disabled style={{ fontSize: 11, padding: "2px 6px" }}>
        复制
      </button>
    );
  }
  return (
    <button
      style={{ fontSize: 11, padding: "2px 6px" }}
      onClick={async () => {
        await navigator.clipboard.writeText(field.value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? "已复制" : "复制"}
    </button>
  );
}

function CopyableRow({ label, field, caption }: { label: string; field: CopyableFieldDto; caption?: string }) {
  return (
    <div className="form-row">
      <label>{label}</label>
      <span style={{ fontFamily: "monospace" }}>{field.value}</span>
      <CopyButton field={field} />
      {(field.caption || caption) && (
        <span style={{ color: "#dc2626", fontSize: 12 }}>{field.caption ?? caption}</span>
      )}
    </div>
  );
}

/**
 * 管理密码字段的掩码/揭示交互,复用 `components/PasswordCell.tsx` 的模式:
 * 默认掩码,点击"显示"才发起单独请求获取明文(调用 `getCredentials`),
 * 15 秒后自动恢复掩码。ADMIN_ONLY——非 Admin 用户看不到"显示"按钮。
 */
function AdminPasswordCell({ instanceId, field }: { instanceId: string; field: CopyableFieldDto }) {
  const { isAdmin } = useAuth();
  const [revealed, setRevealed] = useState<CopyableFieldDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleReveal = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getCredentials(instanceId);
      setRevealed(resp.kiro_go_admin_password);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setRevealed(null), REVEAL_TIMEOUT_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取密码失败");
    } finally {
      setLoading(false);
    }
  };

  const displayField = revealed ?? field;

  return (
    <div className="form-row">
      <label>Kiro_Go_Admin_Password</label>
      <span style={{ fontFamily: "monospace" }}>{revealed ? revealed.value : "••••••••"}</span>
      {isAdmin && !revealed && (
        <button onClick={handleReveal} disabled={loading} style={{ fontSize: 11, padding: "2px 6px" }}>
          {loading ? "加载中..." : "显示"}
        </button>
      )}
      {revealed && <CopyButton field={displayField} />}
      {error && <span style={{ color: "#dc2626", fontSize: 11 }}>{error}</span>}
      {field.caption && <span style={{ color: "#dc2626", fontSize: 12 }}>{field.caption}</span>}
      <span style={{ color: "#94a3b8", fontSize: 11 }}>(该管理面板登录方式仅需密码,不存在独立的用户名字段)</span>
    </div>
  );
}

/**
 * 凭证漂移人工恢复指引文本渲染,与后端 `credential_service.render_recovery_guide`
 * 的文案格式保持一致(Requirements 10.3, 10.4)。`public_ip` 缺失时返回
 * "无法生成指引"提示,不展示含占位符的不完整命令文本。
 */
function renderRecoveryGuide(publicIp: string | undefined): { available: boolean; text?: string; reason?: string } {
  if (!publicIp || publicIp === "-") {
    return { available: false, reason: "台账中未记录该实例的公网 IP 地址,无法生成人工恢复操作指引文本" };
  }
  const text =
    `通过 SSH 登录该 Kiro-Go 实例(公网 IP: ${publicIp}):\n` +
    `  ssh root@${publicIp}\n` +
    `登录后重写管理密码配置并重启容器:\n` +
    `  docker exec kiro-go sh -c "sed -i 's/^ADMIN_PASSWORD=.*/ADMIN_PASSWORD=<在此填写新密码>/' /app/data/config.json"\n` +
    `  docker restart kiro-go\n` +
    `完成后,请在 Fleet Console 详情页使用「标记已手动恢复」提交新密码。`;
  return { available: true, text };
}

/** 场景1：推荐用户条目，只需 email + account_id，key 由系统自动创建 */
interface AutoCreateEntry {
  account_id: string;
  email: string;
  username: string;
}

type AppendKeyMode = "auto" | "manual";

function AppendKeysForm({ instanceId, onDone }: { instanceId: string; onDone: () => void }) {
  const [keyMode, setKeyMode] = useState<AppendKeyMode>("auto");
  // 场景1：推荐用户
  const [autoEntries, setAutoEntries] = useState<AutoCreateEntry[]>([]);
  // 场景2：手动录入
  const [keyEntries, setKeyEntries] = useState<KeyEntryDraft[]>([emptyKeyEntry()]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const [tier, setTier] = useState("");
  const [candidates, setCandidates] = useState<RecommendUserCandidateDto[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);

  const handleTierChange = async (value: string) => {
    setTier(value);
    setCandidateError(null);
    setCandidates([]);
    if (!value) return;
    setLoadingCandidates(true);
    try {
      const resp = await recommendUsers(value);
      setCandidates(resp.candidates);
    } catch (err) {
      setCandidateError(err instanceof Error ? err.message : "获取推荐用户失败");
    } finally {
      setLoadingCandidates(false);
    }
  };

  // 场景1：添加推荐用户
  const handleUseCandidate = (candidate: RecommendUserCandidateDto) => {
    const exists = autoEntries.some((e) => e.email === candidate.email);
    if (!exists) {
      setAutoEntries((prev) => [
        ...prev,
        { account_id: candidate.account_id, email: candidate.email, username: candidate.username },
      ]);
    }
  };

  // 场景2：手动行操作
  const handleAddRow = () => setKeyEntries((prev) => [...prev, emptyKeyEntry()]);
  const handleRemoveRow = (idx: number) => setKeyEntries((prev) => prev.filter((_, i) => i !== idx));
  const handleUpdateRow = (idx: number, patch: Partial<KeyEntryDraft>) =>
    setKeyEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (keyMode === "auto") {
      // 场景1：推荐用户，自动创建 ksk_
      if (autoEntries.length === 0) {
        setError("请至少选择 1 个推荐 Kiro 用户");
        return;
      }
      if (autoEntries.length > MAX_APPEND_KEYS) {
        setError(`单次追加数量不能超过 ${MAX_APPEND_KEYS} 个`);
        return;
      }
      setError("正在自动创建 Kiro API Key，请稍候...");
      setSubmitting(true);
      try {
        const resolvedKeys: { key: string; kiro_user_email: string }[] = [];
        const autoErrors: string[] = [];
        for (const entry of autoEntries) {
          try {
            const resp = await createKiroApiKey(entry.email, entry.account_id);
            if (resp.status === "SUCCESS" && resp.ksk_key) {
              resolvedKeys.push({ key: resp.ksk_key, kiro_user_email: entry.email });
            } else {
              autoErrors.push(`${entry.email}：${resp.error_detail ?? "自动创建失败(NEEDS_MANUAL)"}`);
            }
          } catch (err) {
            autoErrors.push(`${entry.email}：${err instanceof Error ? err.message : "请求失败"}`);
          }
        }
        if (autoErrors.length > 0) {
          setError("部分用户 Key 创建失败：\n" + autoErrors.join("\n"));
          return;
        }
        setError(null);
        const resp = await appendKeys(instanceId, resolvedKeys);
        setResult(`追加成功,本次新增 ${resp.added_count} 个,当前累计 ${resp.kiro_api_key_count} 个`);
        setAutoEntries([]);
        onDone();
      } catch (err) {
        setError(err instanceof Error ? err.message : "追加失败");
      } finally {
        setSubmitting(false);
      }
    } else {
      // 场景2：手动录入
      const nonEmptyEntries = keyEntries.filter((entry) => entry.key.trim().length > 0);
      if (nonEmptyEntries.length === 0) {
        setError("请至少录入 1 个 Kiro_API_Key");
        return;
      }
      if (nonEmptyEntries.length > MAX_APPEND_KEYS) {
        setError(`单次追加数量不能超过 ${MAX_APPEND_KEYS} 个`);
        return;
      }
      const badFormatIdx = nonEmptyEntries.findIndex((e) => !e.key.trim().startsWith("ksk_"));
      if (badFormatIdx >= 0) {
        setError(`第 ${badFormatIdx + 1} 个 Kiro_API_Key 格式不合法(必须以 ksk_ 为前缀)`);
        return;
      }
      const missingEmailIdx = nonEmptyEntries.findIndex((e) => !e.email.trim());
      if (missingEmailIdx >= 0) {
        setError(`第 ${missingEmailIdx + 1} 个 Kiro_API_Key 缺少关联的 Kiro 用户邮箱`);
        return;
      }
      const badEmailIdx = nonEmptyEntries.findIndex((e) => !isValidEmail(e.email.trim()));
      if (badEmailIdx >= 0) {
        setError(`第 ${badEmailIdx + 1} 个 Kiro_API_Key 关联的邮箱格式不合法`);
        return;
      }
      setSubmitting(true);
      try {
        const resp = await appendKeys(
          instanceId,
          nonEmptyEntries.map((e) => ({ key: e.key.trim(), kiro_user_email: e.email.trim() }))
        );
        setResult(`追加成功,本次新增 ${resp.added_count} 个,当前累计 ${resp.kiro_api_key_count} 个`);
        setKeyEntries([emptyKeyEntry()]);
        onDone();
      } catch (err) {
        setError(err instanceof Error ? err.message : "追加失败");
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="error-banner" style={{ whiteSpace: "pre-line" }}>
          {error}
        </div>
      )}
      {result && (
        <div className="card" style={{ background: "#f0fdf4", color: "#166534" }}>
          {result}
        </div>
      )}

      {/* 模式切换 */}
      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <label style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input type="radio" checked={keyMode === "auto"} onChange={() => setKeyMode("auto")} />
          <span>推荐 Kiro 用户<span style={{ fontSize: 11, color: "#64748b", marginLeft: 4 }}>(自动生成 ksk_)</span></span>
        </label>
        <label style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input type="radio" checked={keyMode === "manual"} onChange={() => setKeyMode("manual")} />
          <span>手动录入 Kiro_API_Key</span>
        </label>
      </div>

      {/* 场景1：推荐用户 */}
      {keyMode === "auto" && (
        <div>
          {autoEntries.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>已选用户（创建时自动生成 ksk_）：</div>
              {autoEntries.map((entry) => (
                <div key={entry.email} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontFamily: "monospace" }}>{entry.email}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>({entry.username})</span>
                  <button type="button" style={{ fontSize: 11, padding: "1px 6px" }}
                    onClick={() => setAutoEntries((prev) => prev.filter((e) => e.email !== entry.email))}>
                    移除
                  </button>
                </div>
              ))}
            </div>
          )}
          {autoEntries.length === 0 && (
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "4px 0 8px" }}>
              请在下方推荐列表中选择用户，点击"使用"添加
            </p>
          )}
          <div className="form-row" style={{ marginTop: 4 }}>
            <label>按层级筛选推荐用户</label>
            <select value={tier} onChange={(e) => handleTierChange(e.target.value)}>
              <option value="">全部层级</option>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {loadingCandidates && <p style={{ fontSize: 12, color: "#64748b" }}>加载中...</p>}
          {candidateError && <span style={{ color: "#dc2626", fontSize: 12 }}>{candidateError}</span>}
          {!loadingCandidates && tier && !candidateError && (
            <ul style={{ fontSize: 12, paddingLeft: 16, marginTop: 4 }}>
              {candidates.length === 0 && <li style={{ listStyle: "none", marginLeft: -16 }}>暂无候选用户</li>}
              {candidates.map((c) => {
                const alreadySelected = autoEntries.some((e) => e.email === c.email);
                return (
                  <li key={c.account_id}>
                    {c.email}({c.username}){" "}
                    <button type="button" style={{ fontSize: 11, padding: "1px 6px" }}
                      onClick={() => handleUseCandidate(c)} disabled={alreadySelected}>
                      {alreadySelected ? "已选" : "使用"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* 场景2：手动录入 */}
      {keyMode === "manual" && (
        <div>
          {keyEntries.map((entry, idx) => (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "center" }}>
              <input
                style={{ width: 200, fontFamily: "monospace" }}
                value={entry.key}
                onChange={(e) => handleUpdateRow(idx, { key: e.target.value })}
                placeholder="ksk_xxx"
              />
              <input
                style={{ width: 200 }}
                value={entry.email}
                onChange={(e) => handleUpdateRow(idx, { email: e.target.value })}
                placeholder="关联的 Kiro 用户邮箱"
              />
              <button type="button" onClick={() => handleRemoveRow(idx)} disabled={keyEntries.length <= 1}>
                移除
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddRow}>添加 Key</button>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? "提交中..." : "追加"}
        </button>
      </div>
    </form>
  );
}

function ResendNotificationButton({ instanceId }: { instanceId: string }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async () => {
    setSending(true);
    setError(null);
    setSent(null);
    try {
      const resp = await resendNotification(instanceId);
      setSent(resp.sent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "发送失败");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: "#475569" }}>
        向该实例的使用人邮箱重新发送部署完成通知(含接入地址、API 端点、Client API Key 手动设置提示)。
      </p>
      {error && <div className="error-banner">{error}</div>}
      {sent === true && <p style={{ color: "#16a34a", fontSize: 13 }}>✓ 邮件已发送</p>}
      {sent === false && <p style={{ color: "#dc2626", fontSize: 13 }}>发送失败(请检查 SES 沙盒设置)</p>}
      <button className="primary" onClick={handleResend} disabled={sending}>
        {sending ? "发送中..." : "重新发送通知邮件"}
      </button>
    </div>
  );
}

function MarkRecoveredForm({ instanceId, onDone }: { instanceId: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !password.trim()) {
      setError("需要录入新密码后才能完成标记");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await markRecovered(instanceId, password);
      setPassword("");
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "标记失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="error-banner">{error}</div>}
      <div className="form-row">
        <label>新 Kiro_Go_Admin_Password</label>
        <input
          type="text"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ width: 260 }}
        />
      </div>
      <button className="primary" type="submit" disabled={submitting}>
        {submitting ? "提交中..." : "标记已手动恢复"}
      </button>
    </form>
  );
}

/**
 * Kiro-Go 实例详情页,对应 design.md > 前端页面设计 `FleetDetailPage.tsx`
 * (Requirements 4.1, 4.2, 5.1-5.7, 10.2-10.8)。
 */
export function FleetDetailPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const detail = useAsync<FleetInstanceViewDto>(
    () => getInstance(instanceId ?? ""),
    [instanceId]
  );

  if (!instanceId) return <p>缺少实例标识</p>;
  if (detail.loading) return <p>加载中...</p>;
  if (detail.error) return <div className="error-banner">{detail.error}</div>;
  if (!detail.data) return null;

  const inst = detail.data;
  const isReady = inst.status === "READY";
  const guide = inst.credential_drift ? renderRecoveryGuide(inst.public_ip) : null;

  return (
    <div>
      <h2 className="section-title">实例详情:{inst.instance_id}</h2>

      <div className="card">
        <div className="form-row">
          <label>状态</label>
          <span className="badge badge-pending">{inst.status}</span>
        </div>
        <div className="form-row">
          <label>使用人邮箱</label>
          <span>{inst.assigned_user_email}</span>
        </div>
        <div className="form-row">
          <label>名称/备注</label>
          <span>{inst.name_note ?? "-"}</span>
        </div>
        <div className="form-row">
          <label>部署区域</label>
          <span>{inst.region}</span>
        </div>
        <div className="form-row">
          <label>公网 IP</label>
          <span>{inst.public_ip}</span>
        </div>
        <div className="form-row">
          <label>启动完成时间</label>
          <span>{formatBeijingTime(inst.ready_at)}</span>
        </div>
        <div className="form-row">
          <label>到期时间</label>
          <span>{formatBeijingDate(inst.expires_at)}</span>
        </div>
        <div className="form-row">
          <label>创建时间</label>
          <span>{formatBeijingTime(inst.created_at)}</span>
        </div>
      </div>

      {inst.credential_drift && (
        <div className="card" style={{ background: "#fee2e2", border: "1px solid #dc2626" }} data-testid="drift-banner">
          <strong>凭证可能已失效</strong>:该实例的管理凭证连续多次鉴权失败,可能是使用人自行更改了
          管理面板密码。
          {guide && guide.available ? (
            <pre style={{ whiteSpace: "pre-wrap", background: "#fff", padding: 8, marginTop: 8 }}>
              {guide.text}
            </pre>
          ) : (
            <p style={{ marginTop: 8 }}>{guide?.reason}</p>
          )}
        </div>
      )}

      <div className="card">
        <div className="section-title">接入信息</div>
        {!isReady && <p style={{ color: "#64748b", fontSize: 13 }}>实例尚未部署完成(READY),以下字段暂不可用。</p>}
        <CopyableRow label="API 端点地址(基础)" field={inst.api_endpoint_url} />
        {/* 三个真实 Kiro-Go 兼容 API 端点,用于配置 Claude Code/VSCode/Cline/Continue 等工具 */}
        <div className="form-row">
          <label>Claude API</label>
          <span style={{ fontFamily: "monospace" }}>
            {isReady && inst.api_endpoint_url.value && inst.api_endpoint_url.value !== "-"
              ? `${inst.api_endpoint_url.value}/v1/messages`
              : "-"}
          </span>
          {isReady && inst.api_endpoint_url.value && inst.api_endpoint_url.value !== "-" && (
            <button
              style={{ fontSize: 11, padding: "2px 6px" }}
              onClick={async () => {
                await navigator.clipboard.writeText(`${inst.api_endpoint_url.value}/v1/messages`);
              }}
            >
              复制
            </button>
          )}
          {(!isReady || !inst.api_endpoint_url.value || inst.api_endpoint_url.value === "-") && (
            <button disabled style={{ fontSize: 11, padding: "2px 6px" }}>复制</button>
          )}
          <span style={{ color: "#64748b", fontSize: 11 }}>Anthropic /v1/messages 兼容</span>
        </div>
        <div className="form-row">
          <label>OpenAI API</label>
          <span style={{ fontFamily: "monospace" }}>
            {isReady && inst.api_endpoint_url.value && inst.api_endpoint_url.value !== "-"
              ? `${inst.api_endpoint_url.value}/v1/chat/completions`
              : "-"}
          </span>
          {isReady && inst.api_endpoint_url.value && inst.api_endpoint_url.value !== "-" && (
            <button
              style={{ fontSize: 11, padding: "2px 6px" }}
              onClick={async () => {
                await navigator.clipboard.writeText(`${inst.api_endpoint_url.value}/v1/chat/completions`);
              }}
            >
              复制
            </button>
          )}
          {(!isReady || !inst.api_endpoint_url.value || inst.api_endpoint_url.value === "-") && (
            <button disabled style={{ fontSize: 11, padding: "2px 6px" }}>复制</button>
          )}
          <span style={{ color: "#64748b", fontSize: 11 }}>OpenAI /v1/chat/completions 兼容</span>
        </div>
        <div className="form-row">
          <label>OpenAI Responses API</label>
          <span style={{ fontFamily: "monospace" }}>
            {isReady && inst.api_endpoint_url.value && inst.api_endpoint_url.value !== "-"
              ? `${inst.api_endpoint_url.value}/v1/responses`
              : "-"}
          </span>
          {isReady && inst.api_endpoint_url.value && inst.api_endpoint_url.value !== "-" && (
            <button
              style={{ fontSize: 11, padding: "2px 6px" }}
              onClick={async () => {
                await navigator.clipboard.writeText(`${inst.api_endpoint_url.value}/v1/responses`);
              }}
            >
              复制
            </button>
          )}
          {(!isReady || !inst.api_endpoint_url.value || inst.api_endpoint_url.value === "-") && (
            <button disabled style={{ fontSize: 11, padding: "2px 6px" }}>复制</button>
          )}
          <span style={{ color: "#64748b", fontSize: 11 }}>OpenAI Responses /v1/responses 兼容</span>
        </div>
        <CopyableRow label="Kiro_Go_Client_API_Key(需手动设置)" field={inst.kiro_go_client_api_key} />
        <CopyableRow label="管理面板地址" field={inst.admin_panel_url} />
        <AdminPasswordCell instanceId={inst.instance_id} field={inst.kiro_go_admin_password} />
        {/* 关联的 Kiro 账号邮箱列表 */}
        {inst.kiro_user_emails && inst.kiro_user_emails.length > 0 && (
          <div className="form-row">
            <label>关联 Kiro 账号邮箱</label>
            <span style={{ fontSize: 13 }}>{inst.kiro_user_emails.join("、")}</span>
          </div>
        )}
      </div>

      {isReady && (
        <div className="card">
          <div className="section-title">追加 Kiro_API_Key(充值)</div>
          <AppendKeysForm instanceId={inst.instance_id} onDone={detail.reload} />
        </div>
      )}

      {isReady && (
        <div className="card">
          <div className="section-title">重新发送通知邮件</div>
          <ResendNotificationButton instanceId={inst.instance_id} />
        </div>
      )}

      {inst.credential_drift && (
        <div className="card">
          <div className="section-title">标记已手动恢复</div>
          <MarkRecoveredForm instanceId={inst.instance_id} onDone={detail.reload} />
        </div>
      )}
    </div>
  );
}
